import { Container, IDestroyOptions, Sprite, Text, TextStyle } from "pixi.js";
import { GameFont } from "../../../common/GameFont";
import { ShopItem } from "../model/ShopItem";
import { ShopResourcePackage } from "../resources/ShopResourcePackage";
import { HBox } from "../../../common/components/ui/HBox";
import { Event } from "event-system";

export class UsdShopItemContainer extends Container {
    public readonly item: ShopItem;
    public readonly onUsdPackageClick: Event;

    private marketplaceCardClicked: boolean;
    private downMousePosY: number;

    public constructor(
        private readonly shopResources: ShopResourcePackage,
        private readonly shopItemData: ShopItem
    ) {
        super();
        this.onUsdPackageClick = new Event();
        this.build();
        this._internalEventMode = "dynamic";
        this.on("pointerdown", this.cardTouchStartHandle, this);
        this.on("pointerup", this.cardTouchEndHandle, this);
        this.on("pointermove", this.cardTouchMoveHandle, this);
    }

    private build(): void {
        const background = new Sprite(this.shopResources.assets_views_shop_glass_png);

        const shape = new Sprite(this.shopResources.assets_views_shop_shape_green_png);
        shape.position.set(background.x, background.y + background.height - shape.height);

        const diceBox = new HBox(20);
        const diceIcon = new Sprite(this.shopResources.assets_views_shop_dice_icon_png);
        const diceCountText = new Text(this.shopItemData.reward.diceRolls, new DiceCountTextStyle());
        diceBox.addChild(diceIcon, diceCountText);
        diceCountText.position.y = diceIcon.height * 0.5 - diceCountText.height * 0.5 + 7;
        diceBox.position.set(background.width * 0.5 - diceBox.width * 0.5, 30);

        const itemPriceText = new Text(`${this.shopItemData.price} ${this.shopItemData.currency}`, new ItemPriceTextStyle());
        itemPriceText.position.set(background.width * 0.5 - itemPriceText.width * 0.5, 220);

        this.addChild(background, shape, diceBox, itemPriceText);

        if (this.shopItemData.valueForOffLabel) {
            const discountText = new Text(`${this.shopItemData.valueForOffLabel}% OFF`, new DiceCountTextStyle());
            discountText.style.fontSize = 32;
            discountText.position.set(220, 168);
            this.addChild(discountText);
        }
        if (this.shopItemData.flags.isPopular) {
            shape.texture = this.shopResources.assets_views_shop_shape_violet_png;
            const popularText = new Text('POPULAR', new DiceCountTextStyle());
            popularText.style.fontSize = 32;
            popularText.position.set(background.width * 0.5 - popularText.width * 0.5, 275);
            this.addChild(popularText);
            return;
        }
        if (this.shopItemData.flags.isBestValue) {
            shape.texture = this.shopResources.assets_views_shop_shape_orange_png;
            const bestText = new Text('BEST VALUE', new DiceCountTextStyle());
            bestText.style.fontSize = 32;
            bestText.position.set(background.width * 0.5 - bestText.width * 0.5, 275);
            this.addChild(bestText);
        }
    }

    private async cardTouchStartHandle(event: any): Promise<void> {
        this.marketplaceCardClicked = true;
        this.downMousePosY = Math.ceil(event.data.global.y);
    }

    private async cardTouchMoveHandle(event: any): Promise<void> {
        let currentMousePosY: number = Math.ceil(event.data.global.y);
        if (Math.abs(this.downMousePosY - currentMousePosY) > 20) {
            this.marketplaceCardClicked = false;
        }
    }

    private async cardTouchEndHandle(): Promise<void> {
        if (this.marketplaceCardClicked) {
            this.onUsdPackageClick.emit();
        }
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.off("pointerdown", this.cardTouchStartHandle, this);
        this.off("pointerup", this.cardTouchEndHandle, this);
        this.off("pointermove", this.cardTouchMoveHandle, this);
        super.destroy(options);
    }
}

class DiceCountTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 64,
            dropShadow: true,
            dropShadowAlpha: 0.2,
            dropShadowAngle: 0.9,
            dropShadowBlur: 5
        }));
    }
}

class ItemPriceTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            dropShadow: true,
            dropShadowAngle: 0.1,
            dropShadowBlur: 6,
            dropShadowDistance: 2,
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 56
        }));
    }
}
